import {
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    ConnectedPosition,
} from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    ButtonComponent,
    IconButtonComponent,
    ImageComponent,
} from '@interacta-shared/ui';
import { KeyCode, uuid } from '@interacta-shared/util';
import { ThemeMode } from '@interacta-shared/util-common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { aiThemedAssetSrc } from '../../utils/ai-assets';

export type AIDiscoveryCoachMarkCloseReason = 'button' | 'escape' | 'detach';

const POSITIONS: ConnectedPosition[] = [
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 12,
    },
    {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: 12,
    },
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -12,
    },
];

@Component({
    selector: 'interacta-ai-discovery-coach-mark',
    templateUrl: './ai-discovery-coach-mark.component.html',
    styleUrl: './ai-discovery-coach-mark.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CdkConnectedOverlay,
        IconButtonComponent,
        ImageComponent,
        TranslatePipe,
    ],
})
export class AIDiscoveryCoachMarkComponent {
    private readonly translateService = inject(TranslateService);

    readonly origin = input.required<CdkOverlayOrigin>();
    readonly isOpen = input(false);
    readonly themeMode = input<ThemeMode>('light');

    readonly watchNow = output<void>();
    readonly closing = output<AIDiscoveryCoachMarkCloseReason>();

    readonly descriptionId = uuid();
    readonly positions = POSITIONS;

    readonly brandSrc = computed(() =>
        aiThemedAssetSrc('wordmark', this.themeMode()),
    );

    @ViewChild(CdkConnectedOverlay) overlay?: CdkConnectedOverlay;

    protected readonly announcement = signal('');

    constructor() {
        toObservable(this.isOpen)
            .pipe(takeUntilDestroyed())
            .subscribe((isOpen) =>
                this.announcement.set(
                    isOpen
                        ? this.translateService.instant(
                              'AI.DISCOVERY.COACH_MARK_TITLE',
                          )
                        : '',
                ),
            );

        inject(DestroyRef).onDestroy(() => this.overlay?.overlayRef?.dispose());
    }

    /*
     * Escape is handled on the overlay's own keydown stream rather than through
     * KeyboardEventManagerService: registering there would make this pane the
     * topmost active overlay, and the service traps every key its handler does
     * not expose, cutting arrows, Enter, Tab, Home and End off from the inline
     * contexts underneath.
     */
    onOverlayKeydown(event: KeyboardEvent): void {
        if (event.key === KeyCode.ESCAPE) {
            this.closing.emit('escape');
        }
    }
}
